import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import styles from "../assets/noticeToQuitCreate.module.css";
import Loader from "../components/Loader";
import rentsNoticeToQuitService from "../services/rentsNoticeToQuitService";

const NoticeToQuitCreate = () => {
  const { rentId, id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // Detect if this is a property-based route (/properties/:id/notice-to-quit/create)
  const isPropertyRoute = !!id && !rentId;

  // Get rentId from either path params or query params (rent route only)
  const getRentId = () => {
    if (rentId) return rentId;
    if (isPropertyRoute) return null;
    const urlParams = new URLSearchParams(location.search);
    const rentIdFromQuery = urlParams.get("rent_id");
    return rentIdFromQuery || null;
  };

  const currentRentId = getRentId();
  const currentPropertyId = isPropertyRoute ? id : null;

  const [rent, setRent] = useState(null);
  const [guardians, setGuardians] = useState([]);
  const [property, setProperty] = useState(null);
  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [apiFailed, setApiFailed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dateNotice, setDateNotice] = useState(null);
  const [minDate, setMinDate] = useState(null);
  const [selectedGuardians, setSelectedGuardians] = useState([]);
  const [isGuardian, setIsGuardian] = useState(false);
  const [existingNTQ, setExistingNTQ] = useState(null);

  useEffect(() => {
    if (isPropertyRoute) {
      loadPropertyCreateData();
    } else if (currentRentId) {
      loadCreateData();
      checkExistingNTQ();
    } else {
      setError('Invalid URL. Please navigate from the properties or rents page.');
      setLoading(false);
    }
  }, [currentRentId, currentPropertyId]);

  // ===== Property Route Functions =====

  const loadPropertyCreateData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/properties/${currentPropertyId}/notice-to-quit/create`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setProperty(data.property);

        // Set minDate from backend (already calculated as 28 days from now)
        const minDateObj = data.minDate ? new Date(data.minDate) : new Date();
        if (!data.minDate) {
          minDateObj.setDate(minDateObj.getDate() + 28);
        }
        minDateObj.setHours(0, 0, 0, 0);
        setMinDate(minDateObj);

        // For property route, don't set a default date - user must pick one
        // (Laravel uses a text input, not a pre-selected calendar)
      } else {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.message || errorData.error || `Property ${currentPropertyId} not found`;
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error("Error loading property create data:", error);
      setError(error.message || "Failed to load property data.");
      setApiFailed(true);
      toast.error("Failed to load data from server.");
    } finally {
      setLoading(false);
    }
  };

  const handlePropertySubmit = async (e) => {
    e.preventDefault();
    if (!dateNotice) {
      toast.error("Please enter a notice date");
      return;
    }

    // Confirm if the selected date is within 28 days from now
    const now = new Date();
    const inTwentyEightDays = new Date(now);
    inTwentyEightDays.setDate(inTwentyEightDays.getDate() + 28);
    if (dateNotice < inTwentyEightDays) {
      const confirmed = window.confirm(
        "The selected notice date is within 28 days from now. Are you sure you want to continue?"
      );
      if (!confirmed) return;
    }

    setSubmitting(true);
    try {
      // Format date as YYYY-MM-DD HH:mm (matching Laravel date_format:Y-m-d H:i)
      const year = dateNotice.getFullYear();
      const month = String(dateNotice.getMonth() + 1).padStart(2, '0');
      const day = String(dateNotice.getDate()).padStart(2, '0');
      const hours = String(dateNotice.getHours()).padStart(2, '0');
      const minutes = String(dateNotice.getMinutes()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day} ${hours}:${minutes}`;

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/properties/${currentPropertyId}/notice-to-quit`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({
            date_notice: formattedDate,
          }),
        }
      );

      if (response.ok) {
        toast.success("Notice to quit has been sent!");
        // Navigate back to property show page (matching Laravel redirect)
        navigate(`/properties/${currentPropertyId}`);
      } else {
        const errorData = await response.json();
        // Show server validation errors
        if (errorData.errors && Array.isArray(errorData.errors)) {
          const errorMessages = errorData.errors.map(e => e.msg || e.message).join('. ');
          toast.error(errorMessages || 'Validation failed');
        } else {
          toast.error(errorData.message || "Failed to create notice to quit");
        }
      }
    } catch (error) {
      console.error("Error creating property notice to quit:", error);
      toast.error("Failed to create notice to quit");
    } finally {
      setSubmitting(false);
    }
  };

  // ===== Rent Route Functions =====

  const checkExistingNTQ = async () => {
    try {
      const response = await rentsNoticeToQuitService.getByRentId(currentRentId);
      if (response.success && response.data) {
        setExistingNTQ(response.data);
      }
    } catch (error) {
      console.error("Error checking existing NTQ:", error);
    }
  };

  const handleRemoveNTQ = async () => {
    if (!existingNTQ) return;

    const confirmed = window.confirm(
      "Are you sure you want to remove this Notice to Quit? This action cannot be undone."
    );

    if (!confirmed) return;

    try {
      setSubmitting(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/rents/${currentRentId}/notice-to-quit/destroy`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.ok) {
        toast.success("Notice to quit removed successfully!");
        setExistingNTQ(null);
        navigate("/rents/notice_to_quit");
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || "Failed to remove notice to quit");
      }
    } catch (error) {
      console.error("Error removing NTQ:", error);
      toast.error("Failed to remove notice to quit");
    } finally {
      setSubmitting(false);
    }
  };

  const loadCreateData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/rents/${currentRentId}/notice-to-quit/create`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setRent(data.rent);
        setGuardians(data.guardians || []);
        setProperty(data.property);
        setRoom(data.room);
        // Convert minDate string to Date object
        const minDateObj = data.minDate ? new Date(data.minDate) : new Date();
        minDateObj.setDate(minDateObj.getDate() + 28);
        minDateObj.setHours(0, 0, 0, 0); // Set to start of day for DatePicker
        setMinDate(minDateObj);

        // Set default date to minimum date with time 00:00
        const defaultDate = new Date(minDateObj);
        defaultDate.setHours(0, 0, 0, 0);
        setDateNotice(defaultDate);

        setIsGuardian(data.isGuardian || false);
      } else {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.message || errorData.error || `Rent ${currentRentId} not found`;
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error("Error loading create data:", error);
      setError(error.message || "Failed to load rent data. Please try again or contact support.");
      setApiFailed(true);
      toast.error("Failed to load data from server. Please refresh the page.");
    } finally {
      setLoading(false);
    }
  };

  const handleGuardianSelection = (guardianId) => {
    setSelectedGuardians((prev) =>
      prev.includes(guardianId)
        ? prev.filter((gid) => gid !== guardianId)
        : [...prev, guardianId]
    );
  };

  const handleRentSubmit = async (e) => {
    e.preventDefault();
    if (!dateNotice || (!isGuardian && selectedGuardians.length === 0)) {
      toast.error("Please select guardians and enter a notice date");
      return;
    }

    setSubmitting(true);
    try {
      const formattedDate = dateNotice.toISOString();

      let guardianIds;
      if (isGuardian) {
        const placedGuardians = guardians.filter(g => g.status?.name === 'Placed');
        guardianIds = placedGuardians.map(g => g.id);
      } else {
        guardianIds = selectedGuardians;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/rents/${currentRentId}/notice-to-quit/create`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({
            date_notice: formattedDate,
            guardian_ids: guardianIds,
          }),
        }
      );

      if (response.ok) {
        toast.success("Notice to quit created successfully!");
        navigate("/rents/notice_to_quit");
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || "Failed to create notice to quit");
      }
    } catch (error) {
      console.error("Error creating notice to quit:", error);
      toast.error("Failed to create notice to quit");
    } finally {
      setSubmitting(false);
    }
  };

  // ===== Shared Functions =====

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB");
    } catch (error) {
      return dateString;
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB") + " " +
        String(date.getHours()).padStart(2, '0') + ":" +
        String(date.getMinutes()).padStart(2, '0');
    } catch (error) {
      return dateString;
    }
  };

  // ===== Loading / Error States =====

  if (loading) return <Loader />;

  // Property route error state
  if (isPropertyRoute && (error || !property)) {
    return (
      <div style={{ backgroundColor: '#f0f4f8', minHeight: '100vh', width: '100%' }}>
        <div className={styles.container}>
          <div className={styles.row}>
            <div className={styles.colMd12}>
              <div className={`${styles.alert} ${styles.alertWarning}`}>
                <h4 className={styles.alertHeading}>Error Loading Data</h4>
                <p>{error || 'Failed to load property data.'}</p>
                <div style={{ marginTop: '20px' }}>
                  <button
                    className={`${styles.btn} ${styles.btnHead} ${styles.btnStandard}`}
                    onClick={() => window.location.reload()}
                    style={{ marginRight: '10px' }}
                  >
                    Retry
                  </button>
                  <button
                    className={`${styles.btn} ${styles.btnStandard}`}
                    onClick={() => navigate(`/properties/${currentPropertyId}`)}
                  >
                    Back to Property
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Rent route error state
  if (!isPropertyRoute && (error || !rent)) {
    return (
      <div style={{ backgroundColor: '#f0f4f8', minHeight: '100vh', width: '100%' }}>
        <div className={styles.container}>
          <div className={styles.row}>
            <div className={styles.colMd12}>
              <div className={`${styles.alert} ${styles.alertWarning}`}>
                <h4 className={styles.alertHeading}>Error Loading Data</h4>
                <p>{error || 'Failed to load rent data from database.'}</p>
                <div style={{ marginTop: '20px' }}>
                  <button
                    className={`${styles.btn} ${styles.btnHead} ${styles.btnStandard}`}
                    onClick={() => window.location.reload()}
                    style={{ marginRight: '10px' }}
                  >
                    Retry
                  </button>
                  <button
                    className={`${styles.btn} ${styles.btnStandard}`}
                    onClick={() => navigate('/rents/notice_to_quit')}
                  >
                    Back to Notice To Quit List
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ===== Property Route Layout =====
  // Matches Laravel: properties/notice_to_quit/create.blade.php
  // Simple layout: Property Overview + 28-day info + Notice date input + Send button
  if (isPropertyRoute) {
    return (
      <div style={{ backgroundColor: '#f0f4f8', minHeight: '100vh', width: '100%' }}>
        <div className={styles.container}>
          {/* Breadcrumbs */}
          <div className={styles.breadcrumbContainer}>
            <nav aria-label="breadcrumb">
              <ol className={styles.breadcrumb}>
                <li className={styles.breadcrumbItem}>
                  <Link to="/dashboard">Dashboard</Link>
                </li>
                <li className={styles.breadcrumbItem}>
                  <Link to="/properties">Properties</Link>
                </li>
                <li className={styles.breadcrumbItem}>
                  <Link to={`/properties/${property.id}`}>{property.name}</Link>
                </li>
                <li className={`${styles.breadcrumbItem} ${styles.active}`}>
                  Notice To Quit
                </li>
              </ol>
            </nav>
          </div>

          <div className={styles.row}>
            <div className={styles.colMd12}>
              <div className={`${styles.panel} ${styles.panelDefault} ${styles.panelForm}`}>
                <div className={`${styles.panelHeading} ${styles.panelTitle}`}>
                  <h3>Send Notice to Quit</h3>
                </div>

                <div className={styles.panelBody}>
                  {/* Property Overview - matching Laravel _property_overview partial */}
                  <div className={styles.row}>
                    <div className={styles.colMd12}>
                      <div className={`${styles.panelTitle} ${styles.titleStandard}`}>
                        <h3>Property overview</h3>
                      </div>
                      <div className={`${styles.panelGroup} ${styles.borderDown} ${styles.marginDown}`}>
                        <dl className="list green-list green-list-with-width" style={{ margin: 0 }}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0' }}>
                            <div style={{ width: '100%' }}>
                              <p><strong>Property ID:</strong> {property.property_id}</p>
                              <p><strong>Property Name:</strong> {property.name}</p>
                              <p><strong>Property Status:</strong> {property.statusText}</p>
                              <p><strong>Property address:</strong> {property.address_1}</p>
                              <p><strong>Postcode:</strong> {property.postcode}</p>
                              {property.date_live && (
                                <p>
                                  <strong>Lease dates:</strong>{" "}
                                  {formatDate(property.date_taken_on)}
                                  {property.date_taken_back && ` to ${formatDate(property.date_taken_back)}`}
                                </p>
                              )}
                              {property.date_notice && (
                                <p><strong>Notice date:</strong> {formatDateTime(property.date_notice)}</p>
                              )}
                            </div>
                          </div>
                        </dl>
                      </div>
                    </div>
                  </div>

                  {/* Notice date info text */}
                  <p>Notice dates within 28 days from now (<strong>{minDate ? formatDate(minDate.toISOString()) : ''}</strong>) will require confirmation.</p>

                  {/* Form */}
                  <form onSubmit={handlePropertySubmit}>
                    <fieldset>
                      <div className={styles.formGroup} style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                        <label htmlFor="date_notice" className={styles.controlLabel} style={{ width: '41.66%', textAlign: 'right', paddingRight: '15px' }}>
                          Notice date
                        </label>
                        <div style={{ width: '16.66%' }}>
                          <DatePicker
                            id="date_notice"
                            selected={dateNotice}
                            onChange={(date) => setDateNotice(date)}
                            minDate={new Date()}
                            showTimeSelect
                            timeFormat="HH:mm"
                            timeIntervals={15}
                            dateFormat="dd/MM/yyyy HH:mm"
                            className={styles.formControl}
                            placeholderText="Select date"
                            required
                            timeCaption="Time"
                          />
                        </div>
                      </div>

                      <div style={{ display: 'flex' }}>
                        <div style={{ width: '50%', textAlign: 'right' }}></div>
                        <div style={{ width: '50%' }}>
                          <button
                            type="submit"
                            className={`${styles.btn} ${styles.btnHead} ${styles.btnStandard}`}
                            disabled={submitting || !dateNotice}
                          >
                            <i className={`${styles.glyphicon} ${styles.glyphiconOk}`}></i>{" "}
                            {submitting ? "Sending..." : "Send"}
                          </button>
                        </div>
                      </div>
                    </fieldset>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ===== Rent Route Layout =====
  // Original rent-based NTQ create layout (unchanged)
  return (
    <div style={{ backgroundColor: '#f0f4f8', minHeight: '100vh', width: '100%' }}>
      <div className={styles.container}>
        {/* Breadcrumbs - Laravel structure: Dashboard / Guardians / Rents / Property / Guardian / Notice To Quit */}
        <div className={styles.breadcrumbContainer}>
          <nav aria-label="breadcrumb">
            <ol className={styles.breadcrumb}>
              <li className={styles.breadcrumbItem}>
                <Link to="/dashboard">Dashboard</Link>
              </li>
              <li className={styles.breadcrumbItem}>
                <Link to="/guardians">Guardians</Link>
              </li>
              <li className={styles.breadcrumbItem}>
                <Link to="/rents">Rents</Link>
              </li>
              <li className={styles.breadcrumbItem}>
                <Link to={`/properties/${property?.id}`}>{property?.name}</Link>
              </li>
              {guardians && guardians.length > 0 && guardians[0]?.fullName && guardians[0]?.id && (
                <li className={styles.breadcrumbItem}>
                  <Link to={`/guardians/${guardians[0].id}`}>{guardians[0].fullName}</Link>
                </li>
              )}
              <li className={`${styles.breadcrumbItem} ${styles.active}`}>
                Notice To Quit
              </li>
            </ol>
          </nav>
        </div>

        <div className={styles.row}>
          <div className={styles.colMd12}>
            <div className={`${styles.panel} ${styles.panelDefault} ${styles.panelForm}`}>
              <div className={`${styles.panelHeading} ${styles.panelTitle}`}>
                <h3>Send Notice to Quit</h3>
              </div>

              {/* Existing NTQ Warning/Info */}
              {existingNTQ && (
                <div className={`${styles.alert} ${styles.alertWarning} ${styles.marginDown}`}>
                  <div className={styles.row}>
                    <div className={styles.colMd8}>
                      <h4 className={styles.alertHeading}>Existing Notice to Quit Found</h4>
                      <p>
                        There is already a Notice to Quit for this rent with date: <strong>{formatDate(existingNTQ.date_notice)}</strong>
                      </p>
                      <p>Created by: <strong>{existingNTQ.creator_first_name} {existingNTQ.creator_last_name}</strong></p>
                    </div>
                    <div className={styles.colMd4}>
                      <div className={styles.textRight}>
                        <button
                          type="button"
                          onClick={handleRemoveNTQ}
                          className={`${styles.btn} ${styles.btnDanger} ${styles.btnStandard}`}
                          disabled={submitting}
                        >
                          <i className={`${styles.glyphicon} ${styles.glyphiconRemove}`}></i>{" "}
                          {submitting ? "Removing..." : "Remove NTQ"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className={styles.panelBody}>
                    <form onSubmit={handleRentSubmit}>
                      {isGuardian ? (
                        // Guardian view - simplified
                        <>
                          <div className={styles.row}>
                            <div className={styles.colMd12}>
                              <div className={`${styles.panelTitle} ${styles.titleStandard}`}>
                                <h3>Overview</h3>
                              </div>
                              <div className={`${styles.panelGroup} ${styles.borderDown} ${styles.marginDown}`}>
                                <p>The future date should be a minimum of 28 days from now (<strong>{formatDate(minDate)}</strong>).</p>
                                <p>If you set an earlier date, remember that you will need to pay your licence fee in full and will only receive yours key payment after your living space is checked and keys are returned.</p>
                              </div>
                            </div>
                          </div>

                          <div className={styles.row}>
                            <div className={styles.colMd12}>
                              <div className={`${styles.panelTitle} ${styles.titleStandard}`}>
                                <h3>Leaving Date</h3>
                              </div>
                              <div className={`${styles.panelGroup} ${styles.borderDown} ${styles.marginDown}`}>
                                <div className={styles.formGroup}>
                                  <label htmlFor="date_notice" className={styles.controlLabel}>
                                    Leaving date
                                  </label>
                                  <div className={styles.colSm2}>
                                    <DatePicker
                                      id="date_notice"
                                      selected={dateNotice}
                                      onChange={(date) => setDateNotice(date)}
                                      minDate={minDate}
                                      showTimeSelect
                                      timeFormat="HH:mm"
                                      timeIntervals={15}
                                      dateFormat="dd/MM/yyyy HH:mm"
                                      className={styles.formControl}
                                      placeholderText="Select date and time"
                                      required
                                      inline
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </>
                      ) : (
                        // Staff view - full interface
                        <>
                          {/* Guardian Overview */}
                          <div className={styles.row}>
                            {guardians.map((guardian, index) => (
                              <div key={guardian.id} className={styles.colMd6}>
                                <div className={`${styles.panelTitle} ${styles.titleStandard}`}>
                                  <h3>Guardian {guardians.length > 1 ? `#${index + 1}` : ''}</h3>
                                </div>
                                <div className={`${styles.panelGroup} ${styles.borderDown} ${styles.marginDown}`}>
                                  <div className={styles.textCenter}>
                                    <div className={styles.formGroup}>
                                      <label className={styles.checkboxInline}>
                                        <input
                                          type="checkbox"
                                          checked={selectedGuardians.includes(guardian.id)}
                                          onChange={() => handleGuardianSelection(guardian.id)}
                                          disabled={guardian.status?.name === 'NTQ'}
                                        />
                                        Select Guardian
                                      </label>
                                    </div>
                                    {guardian.status?.name === 'NTQ' && (
                                      <div className={`${styles.alert} ${styles.alertWarning}`}>
                                        <strong>Guardian already has NTQ</strong>
                                      </div>
                                    )}
                                  </div>

                                  {/* Guardian Info */}
                                  <div className={styles.guardianInfo}>
                                    <div className={styles.row}>
                                      <div className={styles.colMd6}>
                                        <p><strong>Guardian ID:</strong> {guardian.unique_guardian_number}</p>
                                        <p><strong>Name:</strong> {guardian.fullName}</p>
                                        <p><strong>Status:</strong> {guardian.status?.name}</p>
                                      </div>
                                      <div className={styles.colMd6}>
                                        <p><strong>Mobile:</strong> {guardian.user?.mobile_number || '-'}</p>
                                        <p><strong>Email:</strong> {guardian.user?.email || '-'}</p>
                                        <p><strong>Hear about:</strong> {guardian.hear_about || '-'}</p>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>

                          {/* Property Overview and Calendar */}
                          <div className={styles.row}>
                            {/* Property Overview */}
                            <div className={styles.colMd6}>
                              <div className={`${styles.panelTitle} ${styles.titleStandard}`}>
                                <h3>Property Overview</h3>
                              </div>
                              <div className={`${styles.panelGroup} ${styles.borderDown} ${styles.marginDown}`}>
                                <div className={styles.row}>
                                  <div className={styles.colMd6}>
                                    <p><strong>Property ID:</strong> {property?.property_id}</p>
                                    <p><strong>Property Name:</strong> {property?.name}</p>
                                    <p><strong>Property Status:</strong> {property?.statusText}</p>
                                    <p><strong>Property address:</strong> {property?.address_1}</p>
                                    <p><strong>Postcode:</strong> {property?.postcode}</p>
                                  </div>
                                  <div className={styles.colMd6}>
                                    <p><strong>Room:</strong> {room?.name}</p>
                                    <p><strong>Rate:</strong> £{room?.price}</p>
                                    <p><strong>Rent Start Date:</strong> {formatDate(rent?.date_start)}</p>
                                    <p><strong>Rent End Date:</strong> {formatDate(rent?.date_end) || '-'}</p>
                                    <p><strong>Keys:</strong> {rent?.keys_provided}</p>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* NTQ Date Calendar */}
                            <div className={styles.colMd6}>
                              <div className={`${styles.panelTitle} ${styles.titleStandard}`}>
                                <h3>NTQ Date</h3>
                              </div>
                              <div className={`${styles.panelGroup} ${styles.marginDown}`}>
                                <div className={styles.row}>
                                  <div className={styles.colMd12}>
                                    <div id="js-rent-ntq" style={{ display: 'flex', justifyContent: 'center' }}>
                                    <DatePicker
                                      id="calendar-input"
                                      selected={dateNotice}
                                      onChange={(date) => setDateNotice(date)}
                                      minDate={minDate}
                                      showTimeSelect
                                      timeFormat="HH:mm"
                                      timeIntervals={15}
                                      dateFormat="dd/MM/yyyy HH:mm"
                                      placeholderText="Select date and time"
                                      required
                                      inline
                                      timeCaption="Time"
                                      showTimeSelectOnly={false}
                                    />
                                    </div>
                                  </div>
                                </div>
                                <p className={styles.textCenter}>
                                  NTQ date should be a minimum of 28 days from now (min. {minDate ? formatDate(minDate.toISOString()) : ''})
                                </p>
                              </div>
                            </div>
                          </div>
                        </>
                      )}

                      {/* Submit Button */}
                      <div className={styles.row}>
                        <div className={styles.colMd12}>
                          <div className={styles.textRight}>
                            <button
                              type="submit"
                              className={`${styles.btn} ${styles.btnHead} ${styles.btnStandard}`}
                              disabled={submitting || !dateNotice || (selectedGuardians.length === 0 && !isGuardian)}
                            >
                              <i className={`${styles.glyphicon} ${styles.glyphiconOk}`}></i>{" "}
                              {submitting ? "Creating..." : "Send NTQ"}
                            </button>
                          </div>
                        </div>
                      </div>
                    </form>
                  </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NoticeToQuitCreate;
